import { Badge, Button, ConfigProvider, Layout, Menu } from 'antd'
import React, { useContext, useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import EnsureLogin from '../EnsureLogin'
import { Color } from '../../Common/Color'
import { deleteRequest } from '../../Common/api'
import GlobalContext from '../GlobalContext'
import { t } from 'i18next'
import { MenuOutlined } from '@ant-design/icons'
import BoxBadge from './BoxBadge'
import ChatBadge from './chatBadge'
import GepetoBadge from './GepetoBadge'

const { Header, Content } = Layout

const currentNotification = () => {
  try {
    return Notification.permission
  } catch {
    return false
  }
}

export default function MenuLayout ({ children }) {
  const navigate = useNavigate()
  const { user, logout, screens } = useContext(GlobalContext)
  const [permission, setPermission] = useState(currentNotification())

  const enablePush = () => {
    Notification.requestPermission().then((status) => {
      setPermission(status)
    })
  }

  const [current, setCurrent] = useState(
    (location.pathname === '/' || location.pathname === '') ? '/admin/appointments_dashboard' : location.pathname
  )

  const logOut = () => {
    deleteRequest({
      url: 'admin/logout',
      params: {},
      onSuccess: () => {
        logout()
        window.location.href = '/admin/login'
      }
    })
  }

  const ALL_VIEWS = {
    appointments: {
      appointments_dashboard: ['admin', 'reader', 'supervisor'],
      appointments_history: ['admin', 'reader', 'supervisor']
    },
    boxes: ['admin', 'reader', 'setup', 'supervisor'],
    chats: ['admin', 'reader', 'setup', 'supervisor'],
    events: ['admin', 'reader', 'setup', 'supervisor'],
    gepetos: ['admin', 'reader'],
    setup: {
      owners: ['admin', 'reader', 'setup'],
      interveners: ['admin', 'reader', 'setup', 'supervisor'],
      users: ['admin', 'reader'],
      teleconsulting_companies: ['admin', 'reader']
    },
    exploitation: {
      statistics: ['admin', 'reader', 'setup', 'supervisor'],
      procedures: ['admin', 'reader', 'setup', 'supervisor'],
    },
    changelog: ['admin', 'reader', 'setup', 'supervisor']
  }

  const item = (key) => {
    if (key === 'changelog' && !user.changelog) {
      return changelogItem()
    } else if (key === 'boxes') {
      return boxItem()
    } else if (key === 'chats') {
      return chatItem()
    } else if (key === 'gepetos') {
      return badgeItem('/admin/gepetos', <GepetoBadge />)
    } else {
      return defaultItem(key)
    }
  }

  const badgeItem = (key, badge) => ({
    key,
    label: (
      <ConfigProvider theme={{
        components: {
          Badge: { colorText: current === key ? Color.primary : Color.black }
        }
      }}>
        {badge}
      </ConfigProvider>
    )
  })

  const boxItem = () => {
    return {
      key: '/admin/boxes',
      label: (
        <ConfigProvider theme={{
          components: {
            Badge: { colorText: current === '/admin/boxes' ? Color.primary : Color.black }
          }
        }}>
          <BoxBadge />
        </ConfigProvider>
      )
    }
  }

  const chatItem = () => {
    return {
      key: '/admin/chats',
      label: (
        <ConfigProvider theme={{
          components: {
            Badge: { colorText: current === '/admin/chats' ? Color.primary : Color.black }
          }
        }}>
          <ChatBadge />
        </ConfigProvider>
      )
    }
  }

  const changelogItem = () => {
    return {
      key: '/admin/changelog',
      label: (
        <Badge.Ribbon text='Nouveau'>
          <Link to={'/admin/changelog'} style={{ fontSize: '24px' }}>
            {t('Admin.Menu.changelog')}
          </Link>
        </Badge.Ribbon>
      )
    }
  }

  const defaultItem = (key) => {
    return { key: `/admin/${key}`, label: (<Link to={`/admin/${key}`}>{t(`Admin.Menu.${key}`)}</Link>) }
  }

  const menuStructure = (views) => {
    const items = []
    for (const key in views) {
      if (!Array.isArray(views[key])) {
        items.push({ key, label: t(`Admin.Menu.${key}`), children: menuStructure(views[key]) })
      } else {
        if (views[key].includes(user.roles[0])) {
          items.push(item(key))
        }
      }
    }
    return items
  }

  const displayMenu = () => {
    const items = menuStructure(ALL_VIEWS)

    items.push({
      key: 'admin/notification',
      label: (
        <div>
          {/* {permission === 'granted' && <p>{t('Admin.EventList.notification.activated')}</p>} */}
          {permission !== 'granted' && <Button onClick={enablePush}>{t('Admin.EventList.notification.button')}</Button>}
        </div>)
    })

    return (
      <>
        <Menu className="admin-menu" style={{ fontSize: '24px', width: '100%', backgroundColor: Color.white, color: Color.dark_blue, height: '60px' }}
          onClick={(e) => setCurrent(e.key)}
          selectedKeys={[current]}
          mode='horizontal'
          items={items} />
        <div style={{ marginLeft: 'auto', marginRight: '2em' }}>
          <Link onClick={logOut}>Déconnexion</Link>
        </div>
      </>
    )
  }

  const compactMenu = () => {
    const items = menuStructure(ALL_VIEWS)
    items.push({ key: 'logout', label: (<Link onClick={logOut} style={{ color: 'red' }}>Déconnexion</Link>) })
    const menu = [{ key: 'visual', label: 'Menu', icon: <MenuOutlined />, children: items }]

    return (
      <Menu className="admin-menu" style={{ fontSize: '24px', minWidth: '5em', backgroundColor: Color.white, color: Color.dark_blue }}
        onClick={(e) => setCurrent(e.key)}
        selectedKeys={[current]}
        mode='vertical'
        expandIcon={<></>}
        triggerSubMenuAction={'click'}
        items={menu} />
    )
  }

  const responsiveMenu = () => {
    if (!user.roles) { return '' }
    if (!screens.xl) {
      return compactMenu()
    } else {
      return displayMenu()
    }
  }

  return (
    <Layout>
      <Header style={{ display: 'flex', padding: 0, backgroundColor: Color.white, color: Color.dark_blue, height: '70px', alignItems: 'center' }}>
        <img onClick={() => navigate('/admin')}
          style={{ cursor: 'pointer', margin: '0px 20px' }}
          src="/images/logo.png" alt='Logo' height={60} />
        {
          responsiveMenu()
        }
      </Header>
      <Content style={{ margin: '20px', marginTop: 0 }}>
        <EnsureLogin>{children || <Outlet />}</EnsureLogin>
      </Content>
    </Layout>
  )
}
